import type { FormikHelpers } from 'formik';
import { Formik } from 'formik';
import isEqual from 'lodash/isEqual';
import type { PropsWithChildren } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import Loader from 'src/components/ui/Loader';
import useAppRouter from 'src/hooks/useAppRouter';
import useUser from 'src/hooks/useUser';
import {
    useCreateAdMutation,
    useHomadataLazyQuery,
    useMyDraftAdQuery,
    useUpdateAdMutation,
} from 'src/hooks/__generated__/queries';
import type { Ad, Maybe } from 'src/types/__generated__/graphql';
import {
    routePathEstimationStep,
    routePathEstimationStep2WithPropertyType,
    routePathProfileEstimateResults,
} from 'src/constants/router';
import useLoginModal from 'src/hooks/useLoginModal';
import {
    estimateSchema,
    type EstimateAdFormValues,
    getEstimateAdVariables,
    getHomaDataVariables,
    getInitialEstimateAdFormValues,
} from 'src/utils/estimateUtils';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent, EventParamKeys } from 'src/utils/analytics';

const EstimateAdLayout: React.FC<PropsWithChildren> = ({ children }) => {
    const router = useAppRouter();
    const { t } = useTranslation(['create-ad', 'common']);
    const currentUser = useUser();
    const [loading, setLoading] = useState(false);
    const propertyType = router.query.propertyType as string;
    const { logEventDynamic } = useDynamicLogEvents();

    const [createAd] = useCreateAdMutation();
    const [updateAd] = useUpdateAdMutation();
    const { data: existingDraftAdData } = useMyDraftAdQuery();
    const [homadata] = useHomadataLazyQuery();
    const { createAuthenticatedHandler } = useLoginModal();

    const existingAd = existingDraftAdData?.me.draftAd;
    const step = router.asPath.includes('step-') ? Number(router.asPath.split('?')[0].split('step-')[1]) : 1;
    const schema = useMemo(() => estimateSchema(t, step), [step, t]);
    const initialValues = getInitialEstimateAdFormValues(existingAd as Maybe<Ad>);

    const handleEstimateAdCreation = async (values: EstimateAdFormValues, userId?: string) => {
        setLoading(true);
        let newHomadata;
        if (!existingAd || !isEqual(initialValues, values)) {
            const { data } = await homadata(getHomaDataVariables(values));
            newHomadata = data;
            if (!newHomadata?.homadata) {
                throw new Error('Could not estimate ad');
            }
        }
        const variables = getEstimateAdVariables(values, newHomadata, existingAd as Ad, userId);
        if (values.id) {
            await updateAd({
                variables: {
                    id: values.id,
                    ...variables,
                },
                refetchQueries: ['MyDraftAd', 'Ad'],
                awaitRefetchQueries: true,
            });
            await router.push(`${routePathProfileEstimateResults}/${values.id}`);
        } else {
            const { data: createdAd } = await createAd({
                variables,
                refetchQueries: ['MyDraftAd'],
                awaitRefetchQueries: true,
            });
            await router.push(`${routePathProfileEstimateResults}/${createdAd?.createAd.id}`);
        }
    };

    const handleForm = async (values: EstimateAdFormValues, formikHelpers: FormikHelpers<EstimateAdFormValues>) => {
        logEventDynamic(AnalyticsEvent[`VisitorEstimateStep${step.toString()}` as keyof typeof AnalyticsEvent], {
            [EventParamKeys.PropertyType]: String(values.propertyType),
        });
        try {
            if (step === 1 && propertyType) {
                // If the propertyType URL param is passed from step 1, we need to pass it to step 2 as well to prefill the field.
                formikHelpers.setTouched({});
                const url = `${routePathEstimationStep2WithPropertyType}${propertyType}`;
                await router.push(url, undefined, {
                    scroll: false,
                });
            } else if (step !== 7) {
                // Formik sets all fields as "touched" on submit, but our multi-step form can be submitted many times
                // and we don't want all fields to be set as "touched" on every step. This resets all the "touched" fields to untouched.
                formikHelpers.setTouched({});
                const url = `${routePathEstimationStep}${step + 1}`;
                await router.push(url, undefined, {
                    scroll: false,
                });
            } else {
                await createAuthenticatedHandler(
                    () => handleEstimateAdCreation(values, currentUser?.id as string),
                    undefined,
                    (userId?: string) => handleEstimateAdCreation(values, userId),
                );
            }
        } catch (error: unknown) {
            // eslint-disable-next-line no-console
            console.log(error);
            setLoading(false);
        }
    };

    return (
        <Formik
            initialValues={initialValues as EstimateAdFormValues}
            validationSchema={schema}
            onSubmit={handleForm}
            enableReinitialize={Boolean(existingAd)}
        >
            {loading ? <Loader /> : children}
        </Formik>
    );
};

export default EstimateAdLayout;
